import React, { useState, useRef, useEffect } from 'react';
import { Tooltip as ReactTooltip } from 'react-tooltip';

const TOOLTIP_ID = 'custom-field-item-tooltip';
// Own id so this rapidly mounting/unmounting anchor (Save/Edit/Copy icons) can never
// share activeAnchor state with unrelated, persistently-mounted anchors on 'general-tooltip'.
const CLOSE_ON_CLICK_EVENTS = { mouseout: true, blur: true, click: true };
const OPEN_WITHOUT_FOCUS_EVENTS = { mouseover: true, focus: false };

// Multiple CustomFieldItem instances can be mounted at once (e.g. salutation + email),
// but react-tooltip renders one floating box per `id`. Only the first mounted instance
// owns the actual <ReactTooltip>, so hovering any instance's icons doesn't pop up duplicates.
let tooltipOwnerCount = 0;

const CustomFieldItem = ({ label, labelNode, name, initialValue, onSave, onCopy }) => {
  const [editing, setEditing] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [committedValue, setCommittedValue] = useState(initialValue ?? '');
  const [value, setValue] = useState(initialValue ?? '');
  const [saving, setSaving] = useState(false);
  const [ownsTooltip, setOwnsTooltip] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (tooltipOwnerCount > 0) return undefined;
    tooltipOwnerCount += 1;
    setOwnsTooltip(true);
    return () => {
      tooltipOwnerCount -= 1;
    };
  }, []);

  useEffect(() => {
    if (!editing) {
      setCommittedValue(initialValue ?? '');
      setValue(initialValue ?? '');
    }
  }, [initialValue, editing]);

  useEffect(() => {
    if (editing && inputRef.current) inputRef.current.focus();
  }, [editing]);

  const cancel = () => {
    setValue(committedValue);
    setEditing(false);
  };

  const save = async () => {
    if (value === committedValue) {
      setEditing(false);
      return;
    }
    setSaving(true);
    try {
      await onSave(value);
      setCommittedValue(value);
      setEditing(false);
    } catch {
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      {labelNode ?? (label ? <h4 className='category-subtitle member-info-subtitle'>{label}</h4> : null)}
      {editing ? (
        <p className='mb-0 member-data d-flex align-items-center specific-top-padding-3'>
          <input
            ref={inputRef}
            type='text'
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') save();
              if (e.key === 'Escape') cancel();
            }}
            className='crm-table-editable-text-input flex-grow-1'
          />
          {saving ? (
            <span className='ml-1 bg-light-blue rounded-circle d-flex align-items-center justify-content-center circle-button-24'>
              <i className='fas fa-spinner fa-spin text-white paragraph-p3' />
            </span>
          ) : (
            <span
              className='icon-circle-check ml-1 text-light-blue title-h2 pointer hover-opacity-85'
              onMouseDown={(e) => e.preventDefault()}
              onClick={save}
              data-tooltip-content='Save'
              data-tooltip-id={TOOLTIP_ID}
            />
          )}
        </p>
      ) : (
        <p
          className='mb-0 member-data mt-2 d-flex align-items-center'
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
        >
          {committedValue ? (
            <>
              <span className='line-height-1-5-rem'>{committedValue}</span>
              {onCopy && (
                <button
                  className='general-button member-email-button'
                  onClick={onCopy}
                  data-tooltip-content='Copy'
                  data-tooltip-id={TOOLTIP_ID}
                >
                  <span className='icon-copy-thin paragraph-p4 light-60-text font-weight-normal d-inline-block' />
                </button>
              )}
              {isHovered && (
                <span
                  className='ml-2 bg-light-blue rounded-circle d-inline-flex align-items-center justify-content-center circle-button-24 flex-shrink-0 pointer hover-opacity-85'
                  onClick={() => {
                    setValue(committedValue);
                    setEditing(true);
                  }}
                  data-tooltip-content='Edit for your team'
                  data-tooltip-id={TOOLTIP_ID}
                >
                  <span className='icon-edit-pencil text-white paragraph-p5' />
                </span>
              )}
            </>
          ) : (
            <>
              <span className='crm-table-editable-text-placeholder line-height-1-5-rem'>
                Add {name ?? label?.toLowerCase()}
              </span>
              {isHovered && (
                <span
                  className='ml-2 bg-light-blue rounded-circle d-inline-flex align-items-center justify-content-center circle-button-24 flex-shrink-0 pointer hover-opacity-85'
                  onClick={() => {
                    setValue('');
                    setEditing(true);
                  }}
                  data-tooltip-content='Edit for your team'
                  data-tooltip-id={TOOLTIP_ID}
                >
                  <span className='icon-edit-pencil text-white paragraph-p5' />
                </span>
              )}
            </>
          )}
        </p>
      )}
      {ownsTooltip && (
        <ReactTooltip
          effect={'solid'}
          id={TOOLTIP_ID}
          className={'header-tooltip'}
          arrowColor={'transparent'}
          place={'top'}
          closeEvents={CLOSE_ON_CLICK_EVENTS}
          openEvents={OPEN_WITHOUT_FOCUS_EVENTS}
        />
      )}
    </>
  );
};

export default CustomFieldItem;
